import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmDialog, Input } from '@bliks-io/ui';

interface DiscardCloneConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDiscarding?: boolean;
  tenantName: string;
}

/**
 * Type-the-name confirmation for discarding a half-built clone, the same gate
 * `DeleteTenantConfirmDialog` puts in front of deleting a tenant — because it
 * is the same destruction: the discard drops the clone's database, deletes its
 * tenant row and revokes the memberships provisioning created.
 */
export function DiscardCloneConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isDiscarding = false,
  tenantName
}: DiscardCloneConfirmDialogProps) {
  const { t } = useTranslation();
  const [confirmInput, setConfirmInput] = useState('');

  const isConfirmed = confirmInput === tenantName;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setConfirmInput('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={t('tenantAdmin.cloneDiscardTitle')}
      description={t('tenantAdmin.cloneDiscardDescription', { name: tenantName })}
      confirmLabel={t('tenantAdmin.cloneDiscard')}
      variant="danger"
      onConfirm={onConfirm}
      isPending={isDiscarding}
      isConfirmDisabled={!isConfirmed}
    >
      <p className="text-sm text-muted-foreground mb-3">
        <Trans
          i18nKey="tenantAdmin.deleteConfirmInstruction"
          components={{
            strong: <strong className="text-foreground font-semibold">{tenantName}</strong>
          }}
        />
      </p>
      <Input
        value={confirmInput}
        onChange={(e) => setConfirmInput(e.target.value)}
        placeholder={t('tenantAdmin.deleteConfirmPlaceholder')}
        autoComplete="off"
      />
    </ConfirmDialog>
  );
}
